// Firebase config — prod + sandbox switched at build time via VITE_ENV.
// Build: `npm run build` (prod) or `npm run build:sandbox` (sandbox).
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import {
  initializeFirestore, persistentLocalCache, persistentMultipleTabManager,
} from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { getMessaging, isSupported as isMessagingSupported } from 'firebase/messaging';

// Web Push VAPID public key — generated in Firebase Console →
// Cloud Messaging → Web configuration. Identifies the Bayless app to
// push services (Apple, Google, Mozilla). NOT secret — bundled into
// the client JS by design. The matching private key stays in Firebase.
export const VAPID_KEY =
  'BLs85cWuBrtE7OHlqye89ITJbrA-aguZw-BBjgTTSIY8KanWiWa5o_PABqOHQySoJyz4dqJigR4E8et1-QWx6rc';

// authDomain MUST match the domain the app is served from — otherwise the
// post-OAuth handler at authDomain/__/auth/handler can't hand state back to
// the app domain (modern browsers block that as third-party-cookie).
// Prod is now served at bayless.app (custom domain), so authDomain follows.
// Pre-req for this to work: the Google Cloud OAuth Web Client must list
// `https://bayless.app/__/auth/handler` as an authorized redirect URI and
// `https://bayless.app` as an authorized JavaScript origin. See:
// https://firebase.google.com/docs/auth/web/redirect-best-practices
const PROD = {
  apiKey: 'AIzaSyDsr99qvvJV82Q8V19UHXnh3kGsTG6NEfY',
  authDomain: 'bayless.app',
  projectId: 'mobile-mechanic-platform-prod',
  storageBucket: 'mobile-mechanic-platform-prod.firebasestorage.app',
  messagingSenderId: '1086358086256',
  appId: '1:1086358086256:web:745ad319246b1b40c64ab5',
};

const SANDBOX = {
  apiKey: 'AIzaSyC0Fv_c8pjWGFhqcf_szIVPs090lhFnDYg',
  authDomain: 'mobile-mechanic-plat-sandbox.web.app',
  projectId: 'mobile-mechanic-plat-sandbox',
  storageBucket: 'mobile-mechanic-plat-sandbox.firebasestorage.app',
  messagingSenderId: '792530271374',
  appId: '1:792530271374:web:5040458d73f2ea76465ddf',
};

export const IS_SANDBOX = import.meta.env.VITE_ENV === 'sandbox';
const firebaseConfig = IS_SANDBOX ? SANDBOX : PROD;

const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
// Firestore with on-disk persistence.
//
// This app is used by MOBILE mechanics — driveways, parking garages,
// rural roads. Without a persistent cache Firestore keeps everything
// in memory only, so relaunching the PWA with no signal showed an
// empty app: no customers, no jobs, no schedule. The data was fine on
// the server and completely unreachable.
//
// persistentLocalCache stores it in IndexedDB, so a cold launch
// offline shows the last known state and queued writes flush when
// signal returns. persistentMultipleTabManager keeps that working
// when the app is open in more than one tab (the older
// enableIndexedDbPersistence would fail outright on the second tab).
//
// Note this does NOT make transactions work offline — they still
// require a round trip, which is why job-number allocation is
// deliberately best-effort. See lib/job-number.js.
//
// Falls back to the default in-memory cache if persistence can't be
// enabled (private browsing, a browser with IndexedDB disabled).
// Degraded is far better than a blank screen.
let firestore;
try {
  firestore = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  });
} catch (e) {
  console.error('Offline persistence unavailable — falling back to memory cache:', e);
  firestore = initializeFirestore(app, {});
}
export const db = firestore;
export const storage = getStorage(app);
export const functions = getFunctions(app);

// FCM (Web Push) — wrapped because not every browser supports it
// (Safari only since iOS 16.4 PWA-installed). Returns null when
// unsupported so callers can gracefully fall back. The actual
// initialization is deferred to first use to avoid breaking
// non-Safari browsers that DO support messaging in newer versions.
export async function getFcmMessaging() {
  if (typeof window === 'undefined') return null;
  try {
    const ok = await isMessagingSupported();
    if (!ok) return null;
    return getMessaging(app);
  } catch (_) {
    return null;
  }
}
export { app };
